import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import styles from './ModalMoveToProject.module.scss';
import { ModalComponent } from 'new/routes/RouterManager';
import { ModalOutProps } from 'new/routes/ModalDefault';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  ASSIGN_FLOW_TO_PROJECT_REQUEST,
  CREATE_PROJECT_ASSIGN_FLOW_REQUEST,
  GET_PROJECTS_REQUEST,
} from 'js/constants/actionTypes';
import { Stack } from 'new/elements/Stack';
import { Button } from 'new/elements/Button';
import { TextField } from 'new/elements/TextField';
import { ProjectsList } from 'new/components/pipelines/ProjectsList/List';
import { Project } from 'new/entities/Project';
import { ModalsContext } from 'new/routes/ModalsContext';
import { useDispatch, useSelector } from 'react-redux';
import { getProjects, refreshPipeline, updateSearchFilters } from 'js/actions';
import { pick } from 'lodash';
import {
  ACLQueryModeTypes,
  AvailableFilterTypesByView,
  SearchFilterViews,
} from 'js/constants/visibilityFilters';
import { useProjectAdapter } from 'new/state/projects/adapter/useProjectAdapter';

export interface ModalMoveToProjectProps {
  step: string;
  flowIds?: number[];
  contextView?: string;
  onSuccessAssignFlowsToProject?: () => void;
  onSuccessCreateProject?: () => void;
  item: {
    id: number;
    type: string;
    origin_node_id: number;
    step: 'new' | 'move' | 'moveNew';
  };
}

function FooterMove({ onClickMove, onClickNew, selectedProject, onCancel }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" padding="16px">
      <Button onClick={onCancel} variant="text">
        Cancel
      </Button>
      <Stack direction="row" gap="16px">
        <Button variant="outlined" onClick={onClickNew}>
          Move to a New Project
        </Button>
        <Button disabled={!selectedProject} variant="contained" onClick={onClickMove}>
          Move to Project
        </Button>
      </Stack>
    </Stack>
  );
}

function FooterNew({ onClickSave, fields, onCancel }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" padding="16px">
      <Button onClick={onCancel} variant="text">
        Cancel
      </Button>
      <Button disabled={!fields.name} variant="contained" onClick={onClickSave}>
        Save
      </Button>
    </Stack>
  );
}

const modalListingDefaults = {
  currentPage: 1,
  totalPages: null,
  totalItems: null,
  sortCriteria: 'NAME_ASC',
};

function reducer(state, action) {
  switch (action.type) {
    case 'updateCurrentPage':
      return { ...state, currentPage: action.value };
    case 'updateTotalPages':
      return { ...state, totalPages: action.value };
    case 'updateTotalItems':
      return { ...state, totalItems: action.value };
    case 'updateSortCriteria':
      return { ...state, sortCriteria: action.value };
  }
  throw Error('Unknown action: ' + action.type);
}

export const ModalMoveToProject: ModalComponent = (
  props: ModalMoveToProjectProps & ModalOutProps,
) => {
  const { setLoading, setFooter, item, setFullWidth, setTitle } = props;
  const dispatch: any = useDispatch();
  const { closeModal } = useContext(ModalsContext);
  const [fields, setFields] = useState({ name: '', description: '' });
  const scrollRef = useRef();
  const [step, setStep] = useState(props.step || 'move');
  const [selectedProject, setSelectedProject] = useState(null);
  const { assignFlowsToProject, createProjectAssignFlow } = useProjectAdapter();

  const clearProjectSearchFilters = () => {
    dispatch(updateSearchFilters(SearchFilterViews.PROJECTS, []));
  };

  const onCancel = () => {
    closeModal(ModalMoveToProject.modalConfig.name);
  };

  useEffect(
    () => () => clearProjectSearchFilters(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally unmount-only; clearProjectSearchFilters only closes over the stable dispatch fn and the SearchFilterViews.PROJECTS constant
    [],
  );

  const availableFilterTypes = AvailableFilterTypesByView[SearchFilterViews.PROJECTS];
  const loading = useIsLoading(
    GET_PROJECTS_REQUEST,
    CREATE_PROJECT_ASSIGN_FLOW_REQUEST,
    ASSIGN_FLOW_TO_PROJECT_REQUEST,
  );
  const { availableFilters: allAvailableFilters } = useSelector(
    (state: any) => state.app.visibilityFilters,
  );
  const [state, listingDispatch] = useReducer(reducer, modalListingDefaults);

  useEffect(() => {
    setFooter(
      step === 'move' ? (
        <FooterMove
          selectedProject={selectedProject}
          onClickMove={onClickMove}
          onClickNew={onClickNew}
          onCancel={onCancel}
        />
      ) : (
        <FooterNew onClickSave={onClickSave} fields={fields} onCancel={onCancel} />
      ),
    );
  }, [selectedProject, step, fields]);

  useEffect(() => {
    setFullWidth(step === 'move');
  }, [step]);

  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (step === 'move') {
      const { currentPage, sortCriteria } = state;
      const availableFilters = pick(allAvailableFilters, availableFilterTypes);

      const options = { aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key };
      const thunk = getProjects(currentPage, sortCriteria, availableFilters, options);

      dispatch(thunk).then((response: { totalPages: any; totalItems: any }) => {
        listingDispatch({
          type: 'updateTotalPages',
          value: response.totalPages,
        });
        listingDispatch({
          type: 'updateTotalItems',
          value: response.totalItems,
        });
      });
    }

    if (props.contextView === 'uncategorized_pipelines') {
      setTitle('Assign Flows to a Project');
      return;
    }

    if (step === 'newMove') {
      setTitle('Move Flow to a New Project');
    } else if (step === 'new') {
      setTitle('Create Project');
    } else if (step === 'move') {
      setTitle('Move Flow to Project');
    }
  }, [step, setTitle]);

  const onClickSave = () => {
    if (!!fields.name) {
      const flowNodeId = item?.origin_node_id;
      const { currentPage, sortCriteria } = state;
      const availableFilters = pick(allAvailableFilters, availableFilterTypes);

      const originNodeIds = (
        props.flowIds?.length > 0 ? props.flowIds : flowNodeId ? [flowNodeId] : []
      ).filter((id): id is number => id != null);

      createProjectAssignFlow(originNodeIds, fields)
        .then(() => {
          if (props?.contextView === 'uncategorized_pipelines') {
            props?.onSuccessAssignFlowsToProject?.();
            closeModal(ModalMoveToProject.modalConfig.name);
            return;
          }

          if (props?.contextView === 'projects') {
            props?.onSuccessCreateProject?.();
            closeModal(ModalMoveToProject.modalConfig.name);
            return;
          }

          if (flowNodeId) {
            dispatch(refreshPipeline(flowNodeId));
            dispatch(getProjects(currentPage, sortCriteria, availableFilters));
          }
        })
        .catch((error) => {
          console.error('Failed to create project and assign flow:', error);
        })
        .finally(() => {
          closeModal(ModalMoveToProject.modalConfig.name);
        });
    }
  };

  const onClickMove = () => {
    const flowNodeId = item?.origin_node_id;
    const flowIds = (
      props.flowIds?.length > 0 ? props.flowIds : flowNodeId ? [flowNodeId] : []
    ).filter((id): id is number => id != null);

    if (!flowIds.length) {
      console.error('No flow IDs provided');
      return;
    }

    assignFlowsToProject({
      originNodeIds: flowIds,
      projectId: selectedProject.id,
    })
      .then(() => {
        if (props?.contextView === 'uncategorized_pipelines') {
          props?.onSuccessAssignFlowsToProject?.();
          closeModal(ModalMoveToProject.modalConfig.name);
          return;
        }

        if (flowNodeId) {
          dispatch(refreshPipeline(flowNodeId));
          closeModal(ModalMoveToProject.modalConfig.name);
          return;
        }
      })
      .catch((error) => {
        console.error('Failed to assign flows to project:', error);
      });
  };

  const onClickNew = () => {
    setStep('newMove');
  };

  const onItemClick = (item: Project) => {
    setSelectedProject(item);
  };

  const onChangeFields = (e) => {
    setFields((fields) => ({ ...fields, [e.target.name]: e.target.value }));
  };

  const onChangeListingItem = ({ type, value }) => {
    const availableFilters = pick(allAvailableFilters, availableFilterTypes);
    listingDispatch({ type: type, value: value });

    if (type === 'updateCurrentPage') {
      dispatch(getProjects(value, state.sortCriteria, availableFilters));
    } else if (type === 'updateSortCriteria') {
      dispatch(getProjects(state.currentPage, value, availableFilters));
    }
  };

  return (
    <Stack className={styles.container} aria-label="modal-move-to-project-container">
      {step === 'move' ? (
        <>
          <div ref={scrollRef} className={styles.listContainer}>
            <ProjectsList
              selectedProject={selectedProject}
              parentScrollRef={scrollRef}
              hideActions
              hideHeader
              inModal
              listing={state}
              onItemClick={onItemClick}
              onListingChange={onChangeListingItem}
            />
          </div>
        </>
      ) : (
        <Stack padding="16px" gap="16px" alignItems="center">
          <TextField
            name="name"
            className={styles.projectNameInput}
            onChange={onChangeFields}
            label="Name"
          />
          <TextField
            name="description"
            className={styles.projectNameInput}
            onChange={onChangeFields}
            label="Description"
          />
        </Stack>
      )}
    </Stack>
  );
};

ModalMoveToProject.modalConfig = {
  name: 'ModalMoveToProject',
  title: 'Move Flow to Project',
  removeContentPadding: true,
  rootClassName: styles.rootModalContainer,
  contentClassName: styles.contentModalContainer,
  fullWidth: true,
  maxWidth: 'md',
};
